import { Combobox, ComboboxProps, Option, OptionProps, useComboboxFilter } from "@fluentui/react-components";
import { OptionOnSelectData, SelectionEvents } from "Shared/FluentUIUnstableDef";
import React, { useCallback, useMemo, useState } from "react";

import { Dismiss16Regular } from "@fluentui/react-icons";
import { TFieldWrapperComponentProps } from "./WithFormFieldWrapper.types";
import { WithFormFieldWrapper } from "./WithFormFieldWrapper";
import { observer } from "mobx-react";

export type IM365PulseFormComboboxFieldOptions = Array<string> | Array<OptionProps & { key: string }>;
type IM365PulseFormComboboxFieldCreateOption = {
    value: string;
    label: string;
};

export type IM365PulseFormComboboxFieldProps = TFieldWrapperComponentProps<
    Omit<ComboboxProps, "value" | "defaultValue" | "selectedOptions" | "onOptionSelect">
> & {
    clearable?: boolean;
    options?: IM365PulseFormComboboxFieldOptions;
    createOptionFromQuery?: (query: string, selectedOptions: string[], existingOptions: string[]) => IM365PulseFormComboboxFieldCreateOption | undefined;
};

const EmptyArray: string[] = [];

const M365PulseFormComboboxFieldBase = (props: React.PropsWithChildren<IM365PulseFormComboboxFieldProps>) => {
    const {
        formikFieldProps: {
            form: { setFieldValue }
        },
        name,
        options: propOptions,
        clearable,
        ...restProps
    } = props;
    const rawFieldValue: unknown = props.formikFieldProps.field.value;
    const value = Array.isArray(rawFieldValue) ? rawFieldValue.filter((item): item is string => typeof item === "string") : EmptyArray;

    // Determine if multiselect is enabled
    const isMultiselect = !!restProps.multiselect;

    // State for input value (query) and focus
    const [query, setQuery] = useState<string>("");
    const [focused, setFocused] = useState(false);
    const [dynamicOptions, setDynamicOptions] = useState<Array<{ key: string; value: string; text: string; children: string }>>([]);

    // Normalize options to OptionProps[]
    const normalizedOptions = useMemo(
        () =>
            propOptions?.map((option) => {
                if (typeof option === "string") {
                    return { key: option, value: option, text: option, children: option };
                }
                // Ensure value is always a string
                return { ...option, key: option.key, value: option.value ?? option.key, children: option.text ?? option.value ?? option.key };
            }) ?? [],
        [propOptions]
    );

    const mergedOptions = useMemo(() => {
        const existing = new Set(normalizedOptions.map((option) => String(option.value)));
        const appended = dynamicOptions.filter((option) => !existing.has(option.value));
        return [...appended, ...normalizedOptions];
    }, [dynamicOptions, normalizedOptions]);

    const existingOptionValues = useMemo(() => mergedOptions.map((option) => String(option.value)), [mergedOptions]);

    // Filtered children using useComboboxFilter
    const filteredChildren = useComboboxFilter(query, mergedOptions, {
        optionToText: (option) => (typeof option === "string" ? option : option.children),
        ...(props.createOptionFromQuery ? {} : { noOptionsMessage: "No options match your search." })
    });

    const hasMatchedExistingOption = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return mergedOptions.length > 0;
        }

        return mergedOptions.some((option) => {
            const optionText = typeof option === "string" ? option : String(option.children);
            return optionText.toLowerCase().includes(normalizedQuery);
        });
    }, [mergedOptions, query]);

    // Selected options: always an array
    const selectedOptions = Array.isArray(value) ? value : EmptyArray;

    const createOption = useMemo(
        () => props.createOptionFromQuery?.(query, selectedOptions, existingOptionValues),
        [existingOptionValues, props, query, selectedOptions]
    );

    // Clear handler
    const onClear: React.MouseEventHandler<SVGElement> = useCallback(
        (e) => {
            setFieldValue(name, EmptyArray);
            setQuery("");
            e.stopPropagation();
        },
        [name, setFieldValue]
    );

    // Option select handler
    const onOptionSelect = useCallback(
        (event: SelectionEvents, data: OptionOnSelectData) => {
            const createdValue = data.optionValue ?? "";
            if (createdValue && !existingOptionValues.some((option) => option.toLowerCase() === createdValue.toLowerCase())) {
                setDynamicOptions((previous) => {
                    if (previous.some((option) => option.value.toLowerCase() === createdValue.toLowerCase())) {
                        return previous;
                    }

                    return [...previous, { key: createdValue, value: createdValue, text: createdValue, children: createdValue }];
                });
            }

            setFieldValue(name, data.selectedOptions);
            if (isMultiselect) {
                setQuery("");
            } else {
                setQuery(data.optionValue ?? "");
            }
        },
        [existingOptionValues, isMultiselect, name, setFieldValue]
    );

    // Change handler
    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    }, []);

    // Focus handler
    const onFocus = useCallback(() => {
        setFocused(true);
        if (isMultiselect) {
            setQuery("");
        } else {
            setQuery(Array.isArray(value) ? value.join(", ") : "");
        }
    }, [isMultiselect, value]);

    // Blur handler
    const onBlur = useCallback(() => {
        setFocused(false);
        if (isMultiselect) {
            setQuery(Array.isArray(value) ? value.join(", ") : "");
        }
    }, [isMultiselect, value]);

    // Value to display in input
    const inputValue = useMemo(() => {
        const selectedValue = Array.isArray(value) ? value.join(", ") : "";
        return focused ? query : selectedValue;
    }, [focused, query, value]);

    // Expand icon for clearable
    const expandIcon = useMemo(
        () => (clearable && Array.isArray(value) && value.length > 0 ? <Dismiss16Regular onClick={onClear} /> : undefined),
        [onClear, clearable, value]
    );

    return props.children ? (
        <Combobox
            value={inputValue}
            selectedOptions={selectedOptions}
            onOptionSelect={onOptionSelect}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            expandIcon={expandIcon}
            {...restProps}
        >
            {props.children}
        </Combobox>
    ) : (
        <Combobox
            value={inputValue}
            selectedOptions={selectedOptions}
            onOptionSelect={onOptionSelect}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            expandIcon={expandIcon}
            {...restProps}
        >
            <>
                {props.createOptionFromQuery && !hasMatchedExistingOption ? null : filteredChildren}
                {createOption ? <Option value={createOption.value}>{createOption.label}</Option> : null}
            </>
        </Combobox>
    );
};

export const M365PulseFormComboboxField = observer(WithFormFieldWrapper(M365PulseFormComboboxFieldBase));
